import React, { useState, useEffect, useCallback } from 'react'
import {
  Button,
  TextArea,
  Heading,
  Text,
  Toast,
  Box,
  Flex,
  Badge,
} from '@vibe/core'
import { Check, Warning, Settings } from '@vibe/icons'
import { AuthGuard } from '../components/common/AuthGuard'
import { AdminOnly } from '../components/common/PermissionGate'
import { Layout } from '../components/layout'
import { apiService } from '../services/api'
import { getErrorMessage } from '../utils/errorHandling'
import { formatDateTime } from '../utils/dateFormatting'
import type {
  IntegrationConfiguration,
  CreateIntegrationConfigRequest,
  UpdateIntegrationConfigRequest,
} from '../types'

interface FormErrors {
  [key: string]: string
}

const IntegrationsManagement: React.FC = () => {
  // State management
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<{
    message: string
    type: 'positive' | 'negative'
  } | null>(null)

  // Integration data
  const [integrationConfig, setIntegrationConfig] =
    useState<IntegrationConfiguration | null>(null)

  // Form state
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    service_account_credentials: '',
    is_active: true,
  })
  const [errors, setErrors] = useState<FormErrors>({})

  // Load integration data
  const loadIntegrationData = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Load integration configuration only
      try {
        const configResponse = await apiService.getIntegrationConfig()
        setIntegrationConfig(configResponse.data)
        setFormData({
          service_account_credentials: '', // Don't show credentials for security
          is_active: configResponse.data.is_active,
        })
      } catch (err: any) {
        if (err?.status !== 404) {
          // Only show error if it's not a "not found" error (which means no config exists yet)
          console.error('Failed to load integration config:', err)
        }
      }
    } catch (err) {
      console.error('Error loading integration data:', err)
      setError(
        getErrorMessage(err) ||
          'Error al cargar la configuración de integración'
      )
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Initial load
  useEffect(() => {
    loadIntegrationData()
  }, [loadIntegrationData])

  // Form validation
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.service_account_credentials.trim()) {
      newErrors.service_account_credentials =
        'Las credenciales de la cuenta de servicio son obligatorias'
    } else {
      // Validate JSON format
      try {
        const parsed = JSON.parse(formData.service_account_credentials)

        // Basic validation of service account structure
        if (!parsed.type || parsed.type !== 'service_account') {
          newErrors.service_account_credentials =
            'Las credenciales deben ser de tipo "service_account"'
        } else if (!parsed.project_id) {
          newErrors.service_account_credentials =
            'Las credenciales deben incluir "project_id"'
        } else if (!parsed.private_key_id || !parsed.private_key) {
          newErrors.service_account_credentials =
            'Las credenciales deben incluir "private_key_id" y "private_key"'
        } else if (!parsed.client_email) {
          newErrors.service_account_credentials =
            'Las credenciales deben incluir "client_email"'
        }
      } catch {
        newErrors.service_account_credentials =
          'Las credenciales deben estar en formato JSON válido'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData])

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      setToast({
        message: 'Por favor corrige los errores antes de continuar',
        type: 'negative',
      })
      return
    }

    setIsSubmitting(true)

    try {
      if (integrationConfig) {
        // Update existing configuration
        const updateData: UpdateIntegrationConfigRequest = {
          is_active: formData.is_active,
        }

        // Only include credentials if they were changed
        if (formData.service_account_credentials.trim()) {
          updateData.service_account_credentials =
            formData.service_account_credentials
        }

        await apiService.updateIntegrationConfig(updateData)
        setToast({
          message: 'Configuración de integración actualizada exitosamente',
          type: 'positive',
        })
      } else {
        // Create new configuration
        const createData: CreateIntegrationConfigRequest = {
          integration_type: 'google_forms',
          service_account_credentials: formData.service_account_credentials,
        }

        await apiService.createIntegrationConfig(createData)
        setToast({
          message: 'Configuración de integración creada exitosamente',
          type: 'positive',
        })
      }

      // Reload data and exit edit mode
      await loadIntegrationData()
      setIsEditing(false)
      setFormData(prev => ({ ...prev, service_account_credentials: '' }))
    } catch (error) {
      console.error('Error saving integration config:', error)
      setToast({
        message:
          error instanceof Error
            ? error.message
            : 'Error al guardar la configuración',
        type: 'negative',
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [formData, integrationConfig, validateForm, loadIntegrationData])

  // Handle cancel
  const handleCancel = useCallback(() => {
    setIsEditing(false)
    setFormData({
      service_account_credentials: '',
      is_active: integrationConfig?.is_active ?? true,
    })
    setErrors({})
  }, [integrationConfig])

  // Auto-hide toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [toast])

  if (isLoading) {
    return (
      <AuthGuard>
        <Layout>
          <div className="max-w-4xl mx-auto px-6 py-8">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3 mb-8"></div>
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
                <div className="space-y-4">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>
            </div>
          </div>
        </Layout>
      </AuthGuard>
    )
  }

  return (
    <AuthGuard>
      <AdminOnly>
        <Layout>
          <div className="max-w-4xl mx-auto px-6 py-8">
            {/* Toast notifications */}
            {toast && (
              <Toast
                open={true}
                onClose={() => setToast(null)}
                className="fixed top-4 right-4 z-50"
                type={toast.type}
              >
                {toast.message}
              </Toast>
            )}

            {/* Header */}
            <div className="mb-8">
              <Heading type="h1" className="mb-2">
                Gestión de Integraciones
              </Heading>
              <Text color="secondary">
                Configura y administra las integraciones con servicios externos
              </Text>
            </div>

            {/* Error state */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <Warning className="text-red-600 mr-2" />
                  <Text className="text-red-800">{error}</Text>
                </div>
              </div>
            )}

            {/* Google Forms Integration Configuration */}
            <Box className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6">
                <Flex justify="space-between" align="center" className="mb-6">
                  <div>
                    <Heading type="h3" className="mb-2">
                      Integración con Google Forms
                    </Heading>
                    <Text color="secondary">
                      Configura las credenciales de la cuenta de servicio de
                      Google para habilitar la sincronización automática
                    </Text>
                  </div>
                  <div className="flex items-center space-x-2">
                    {integrationConfig && (
                      <Badge>
                        {integrationConfig.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    )}
                    <Settings className="text-secondary-500" />
                  </div>
                </Flex>

                {!isEditing ? (
                  // View mode
                  <div className="space-y-4">
                    {integrationConfig ? (
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <Check className="text-green-600" />
                          <Text>Configuración establecida</Text>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Text className="text-sm" color="secondary">
                                Fecha de creación
                              </Text>
                              <Text>
                                {formatDateTime(integrationConfig.created_at)}
                              </Text>
                            </div>
                            <div>
                              <Text className="text-sm" color="secondary">
                                Última actualización
                              </Text>
                              <Text>
                                {formatDateTime(integrationConfig.updated_at)}
                              </Text>
                            </div>
                          </div>
                        </div>

                        <Flex gap="small">
                          <Button
                            onClick={() => setIsEditing(true)}
                            disabled={isSubmitting}
                          >
                            Actualizar Configuración
                          </Button>
                        </Flex>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Warning
                          className="text-yellow-600 mx-auto mb-4"
                          size={48}
                        />
                        <Heading type="h3" className="mb-2">
                          Integración no configurada
                        </Heading>
                        <Text color="secondary" className="mb-4">
                          Para habilitar la sincronización con Google Forms,
                          necesitas configurar las credenciales de la cuenta de
                          servicio
                        </Text>
                        <Button
                          onClick={() => setIsEditing(true)}
                          disabled={isSubmitting}
                        >
                          Configurar Integración
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  // Edit mode
                  <div className="space-y-6">
                    <div>
                      <TextArea
                        title="Credenciales de la Cuenta de Servicio *"
                        placeholder="Pega aquí el contenido completo del archivo JSON de credenciales de Google Cloud..."
                        value={formData.service_account_credentials}
                        onChange={(
                          event: React.ChangeEvent<HTMLTextAreaElement>
                        ) =>
                          setFormData(prev => ({
                            ...prev,
                            service_account_credentials: event.target.value,
                          }))
                        }
                        rows={10}
                        required
                      />
                      {errors.service_account_credentials && (
                        <Text className="text-red-600 text-sm mt-1">
                          {errors.service_account_credentials}
                        </Text>
                      )}
                      <Text className="text-sm text-secondary-600 mt-2">
                        Las credenciales deben ser de una cuenta de servicio con
                        permisos para acceder a Google Sheets y Google Drive
                      </Text>
                    </div>

                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={formData.is_active}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            is_active: e.target.checked,
                          }))
                        }
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <label
                        htmlFor="is_active"
                        className="text-sm font-medium text-secondary-900"
                      >
                        Activar integración
                      </label>
                    </div>

                    <Flex gap="small">
                      <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        loading={isSubmitting}
                      >
                        {integrationConfig ? 'Actualizar' : 'Crear'}{' '}
                        Configuración
                      </Button>
                      <Button
                        kind="tertiary"
                        onClick={handleCancel}
                        disabled={isSubmitting}
                      >
                        Cancelar
                      </Button>
                    </Flex>
                  </div>
                )}
              </div>
            </Box>

            {/* Help Section */}
            <Box className="bg-white rounded-lg shadow-sm border border-gray-200 mt-6">
              <div className="p-6">
                <Heading type="h3" className="mb-4">
                  Guía de Configuración
                </Heading>
                <div className="space-y-4">
                  <div>
                    <Text className="font-medium mb-2">
                      1. Crear una cuenta de servicio en Google Cloud
                    </Text>
                    <Text className="text-sm text-secondary-600">
                      Ve a la consola de Google Cloud, crea un nuevo proyecto o
                      selecciona uno existente, y crea una cuenta de servicio
                      con permisos para Google Sheets API y Google Drive API.
                    </Text>
                  </div>
                  <div>
                    <Text className="font-medium mb-2">
                      2. Descargar las credenciales
                    </Text>
                    <Text className="text-sm text-secondary-600">
                      Genera una clave JSON para la cuenta de servicio y
                      descarga el archivo de credenciales.
                    </Text>
                  </div>
                  <div>
                    <Text className="font-medium mb-2">
                      3. Configurar permisos
                    </Text>
                    <Text className="text-sm text-secondary-600">
                      Asegúrate de que la cuenta de servicio tenga acceso a las
                      hojas de cálculo que deseas sincronizar. Puedes compartir
                      las hojas directamente con el email de la cuenta de
                      servicio.
                    </Text>
                  </div>
                </div>
              </div>
            </Box>
          </div>
        </Layout>
      </AdminOnly>
    </AuthGuard>
  )
}

export default IntegrationsManagement
